import {
  BREAKPOINT_DESKTOP,
  BREAKPOINT_TABTOP,
  BREAKPOINT_TABLET,
  BREAKPOINT_MOBILE,
} from "../../constants/constants";
import { isEmptyObject } from "../../components/Dashboard/TrendChart/trendChartUtils";
import { objectFromEntries } from "@raven/shared/utils";
import {
  DEFAULT_COMPONENT_DIMENSION_PERCENTS,
  GRID_LAYOUT_COMPONENTS,
} from "./defaultGridLayoutComponents";
import { BreakpointKeysEnum } from "types/enums";
import { ParsedQuery } from "query-string";
import { useSelector } from "store/useSelector";

export const GRID_LAYOUT_MARGIN_OUTER = 10;

type BreakpointsType = {
  [Category in keyof typeof BreakpointKeysEnum]: number;
};

/** must have exact same breakpoint keys as GRID_CELL_TOTALS_BY_BREAKPOINT (e.g. lg, md, sm, etc.) */
export const BREAKPOINTS: BreakpointsType = {
  lg: BREAKPOINT_DESKTOP,
  md: BREAKPOINT_TABTOP,
  sm: BREAKPOINT_TABLET,
  xs: BREAKPOINT_MOBILE,
  xxs: 0, // https://github.com/STRML/react-grid-layout uses 0 for xxs
};

/** must have exact same breakpoint keys as BREAKPOINTS (e.g. lg, md, sm, etc.) */
export const TOTAL_CELLS_IN_LAYOUT_BY_BREAKPOINT = {
  lg: { cols: 100, rows: 100 },
  md: { cols: 100, rows: 100 },
  sm: { cols: 100, rows: 100 },
  xs: { cols: 100, rows: 100 },
  xxs: { cols: 100, rows: 100 },
};

export type GridLayoutComponentsObj = {
  [componentName: string]: {
    shouldDisplay: boolean;
    heightPercent: number;
    widthPercent: number;
  };
};

/** get the object containing all components that will have a data-grid (block) in the GridLayout
 * @param query: the url query from the window location
 * @param breakpointKey: BreakpointKeysEnum string representing the breakpoint key (xxs, xs, sm, md, lg)
 */
export const getGridLayoutComponents = (
  query: ParsedQuery<string>,
  breakpointKey: BreakpointKeysEnum
): GridLayoutComponentsObj => {
  // e.g. /<customer>/grid?trendchartw=50&trendcharth=30
  // const query = getQuery(location);
  if (isEmptyObject(query)) {
    // return the default layout object
    // map the current breakpoint width/height to the default and return it
    return objectFromEntries(
      Object.entries(DEFAULT_COMPONENT_DIMENSION_PERCENTS).map(
        ([componentName, percentsByBreakpoint]) => {
          const { height: heightPercent, width: widthPercent } =
            percentsByBreakpoint[breakpointKey];
          const shouldDisplay = checkShouldDisplay(
            widthPercent,
            heightPercent,
            breakpointKey,
            componentName
          );
          return [
            componentName,
            { heightPercent, widthPercent, shouldDisplay },
          ];
        }
      )
    ) as GridLayoutComponentsObj;
  }
  return {
    [GRID_LAYOUT_COMPONENTS.hubletView]: {
      shouldDisplay: checkShouldDisplay(
        Number(query.hubletvieww),
        Number(query.hubletviewh),
        breakpointKey,
        GRID_LAYOUT_COMPONENTS.hubletView
      ),
      widthPercent: Number(query.hubletvieww),
      heightPercent: Number(query.hubletviewh),
    },
    [GRID_LAYOUT_COMPONENTS.trendChart]: {
      shouldDisplay: checkShouldDisplay(
        Number(query.trendchartw),
        Number(query.trendcharth),
        breakpointKey,
        GRID_LAYOUT_COMPONENTS.trendChart
      ),
      widthPercent: Number(query.trendchartw),
      heightPercent: Number(query.trendcharth),
    },
    [GRID_LAYOUT_COMPONENTS.pnl]: {
      shouldDisplay: checkShouldDisplay(
        Number(query.pnlw),
        Number(query.pnlh),
        breakpointKey,
        GRID_LAYOUT_COMPONENTS.pnl
      ),
      widthPercent: Number(query.pnlw),
      heightPercent: Number(query.pnlh),
    },
    [GRID_LAYOUT_COMPONENTS.timeline]: {
      shouldDisplay: checkShouldDisplay(
        Number(query.timelinew),
        Number(query.timelineh),
        breakpointKey,
        GRID_LAYOUT_COMPONENTS.timeline
      ),
      widthPercent: Number(query.timelinew),
      heightPercent: Number(query.timelineh),
    },
  };
};

const checkShouldDisplay = (
  queryWidthPercent,
  queryHeightPercent,
  breakpointKey,
  componentName
) => {
  // use the query dimensions if it specifies a width or a height percent
  const shouldUseQueryDimensions =
    Boolean(queryWidthPercent) || Boolean(queryHeightPercent);

  if (shouldUseQueryDimensions) {
    // if the query height or width is specified to 0, do not display
    const isHeightOrWidthZero = [
      Number(queryWidthPercent),
      Number(queryHeightPercent),
    ].includes(0);
    return !isHeightOrWidthZero;
  } else {
    // if the default height or width is specified to 0, do not display
    const { height: heightPercent, width: widthPercent } =
      DEFAULT_COMPONENT_DIMENSION_PERCENTS[componentName][breakpointKey];
    const isHeightOrWidthZero = [heightPercent, widthPercent].includes(0);
    return !isHeightOrWidthZero;
  }
};

export const layoutReducer =
  (gridColumns, gridRows, breakpointKey) =>
  (accumulator, [componentName, specs]) => {
    // Can't use the idx param from reducer because it increments
    // everytime we go through this function, but we need only
    // the times an item is added to the accumulator
    const idx = accumulator.length;
    if (
      specs.shouldDisplay &&
      DEFAULT_COMPONENT_DIMENSION_PERCENTS[componentName][breakpointKey]
        .height !== 0 &&
      DEFAULT_COMPONENT_DIMENSION_PERCENTS[componentName][breakpointKey]
        .width !== 0
    ) {
      const widthInColumns = Math.ceil(
        (gridColumns / 100) *
          (Number(specs.widthPercent) ||
            DEFAULT_COMPONENT_DIMENSION_PERCENTS[componentName][breakpointKey]
              .width)
      );
      const heightInRows = Math.ceil(
        (gridRows / 100) *
          (Number(specs.heightPercent) ||
            DEFAULT_COMPONENT_DIMENSION_PERCENTS[componentName][breakpointKey]
              .height)
      );
      let x = 0;
      let y = 0;

      if (idx > 0) {
        const prevGridItem = accumulator[idx - 1];
        // it should stay in the current row if the x so far (this item's x) + this item's width isn't over the limit
        const xIfInCurrentRow = prevGridItem.x + prevGridItem.w;
        const shouldItemStayInCurrentRow =
          xIfInCurrentRow + widthInColumns <= gridColumns;
        if (shouldItemStayInCurrentRow) {
          // increment the x
          x = xIfInCurrentRow;
        }

        const lastGridItemInSameColumn = [...accumulator]
          .reverse()
          .find(item => item.x === x);
        y =
          (lastGridItemInSameColumn?.y || 0) +
          (lastGridItemInSameColumn?.h || 0);
      }
      const gridItem = {
        i: componentName,
        x: x,
        y: y,
        w: widthInColumns,
        h: heightInRows,
        static: true,
      };

      return [...accumulator, gridItem];
    }

    return accumulator;
  };

/** gets the min-width breakpoint key (e.g. lg, md, sm, etc.) */
export const getBreakpointKey = (screenWidth: number): BreakpointKeysEnum => {
  // tslint:disable-next-line: prefer-immediate-return
  const breakpointKey = Object.entries(BREAKPOINTS).reduce(
    (accumulatorKey, [currentBreakpointKey, currentWidth]) => {
      return screenWidth > currentWidth &&
        currentWidth > BREAKPOINTS[accumulatorKey]
        ? (currentBreakpointKey as BreakpointKeysEnum)
        : accumulatorKey;
    },
    BreakpointKeysEnum.xxs // default to xxs
  );
  return breakpointKey;
};

export const useStorybookQuery = (): ParsedQuery<string> => {
  return useSelector(state => state.legacy.storyQuery);
};
